<template>
  <div ref="containerRef" tabindex="-1" class="w-full h-full overflow-y-auto outline-none">
    <div
      v-if="displayData.length === 0"
      class="w-full h-full flex flex-col justify-center items-center"
    >
      <div class="flex flex-col items-start w-32 ml-8.5">
        <div class="relative flex items-center bg-white rounded-md py-1 mb-1.5 px-1">
          <div
            class="px-0.5 text-s-medium text-text-import-default leading-4 border-r border-n-light-default"
          >
            {{ $t('keys.macro.menu.key') }}
          </div>
          <div
            class="px-0.5 text-s-medium text-text-import-default leading-4 border-r border-n-light-default"
          >
            {{ $t('keys.macro.menu.delay') }}
          </div>
          <div class="px-0.5 text-s-medium text-text-import-default leading-4">
            {{ $t('keys.macro.menu.coordinate') }}
          </div>
          <div
            className="absolute -bottom-0.75 left-3 w-1.5 h-1.5 bg-white border-r border-b border-gray-50 rotate-45 transform"
          />
        </div>
        <div class="flex items-center bg-white rounded-md py-1 mb-1.5">
          <div class="px-2 border-r border-n-light-default">
            <Icon icon="Function-Wireframe-Add1" class="w-3.5 h-3.5 text-brand-blue1" />
          </div>
          <div class="px-2 border-r border-n-light-default">
            <Icon icon="Writing-Wireframe-Edit" class="w-3.5 h-3.5" />
          </div>
          <div class="px-2">
            <Icon icon="Function-Wireframe-Delete" class="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <div
          class="flex flex-col justify-center items-center w-8 h-8 rounded-md text-black-white glass-frosted bg-neutral-light-default"
        >
          <span class="text-s-medium text-text-black-white leading-3 mt-1">U</span>
          <Icon icon="Direction-FIll-Down1" class="w-3.5 h-3.5" />
        </div>
        <div class="flex flex-col justify-center items-center w-8 h-8 rounded-md bg-white">
          <span class="text-s-medium text-text-black-white leading-3">26</span>
          <span class="text-s-medium text-text-black-white leading-3">ms</span>
        </div>
        <div
          class="flex flex-col justify-center items-center w-8 h-8 rounded-md text-black-white glass-frosted bg-neutral-light-default"
        >
          <Icon icon="Direction-FIll-Up1" class="w-3.5 h-3.5" />
          <span class="text-s-medium text-text-black-white leading-3 mb-1">U</span>
        </div>
      </div>
      <div className="flex flex-col items-center mt-1 mb-1">
        <div className="flex flex-col gap-0.5 mb-1 opacity-30">
          <div className="w-px h-px bg-gray-600 rounded-full" />
          <div className="w-px h-px bg-gray-600 rounded-full" />
          <div className="w-px h-px bg-gray-600 rounded-full" />
          <div className="w-px h-px bg-gray-600 rounded-full" />
        </div>
        <span className="text-m-medium text-black-white">
          {{ $t('keys.macro.rightClickEdit') }}
        </span>
      </div>
    </div>

    <div v-else class="flex flex-wrap items-center gap-1.75 p-1.5">
      <MacroTag
        v-for="record in displayData"
        :key="record.__id"
        :ref="(el) => registerCardRef(record.__id, el)"
        :record="record"
        :disabled-menu="isRecording"
        :coordinate-min="coordinateMin"
        :coordinate-max="coordinateMax"
        @menu-click="(type) => routeMainAction(type, record, record.__id)"
        @sub-action-click="(type) => routeSubAction(type, record, record.__id)"
        @update:delay="(time) => handleDelayUpdate(record, time)"
        @update:coordinate="(payload) => handleCoordinateUpdate(record, payload)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { toast, Icon } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'

  import type {
    MacroEditorAction,
    MacroEditorKeyAction,
    MacroEditorWheelAction,
    WheelType,
  } from '../../types'
  import { useActionRouter } from '../../composables/useActionRouter'
  import { useCardRefs } from '../../composables/useCardRefs'
  import { useDraftDataManager } from '../../composables/useDraftDataManager'
  import { useMacroRecordingEvents } from '../../composables/useMacroRecordingEvents'
  import { useRecordActions } from '../../composables/useRecordActions'
  import { useRecordMutations } from '../../composables/useRecordMutations'
  import { useSingleCapture } from '../../composables/useSingleCapture'
  import type { DraftRecordItem } from '../../composables/useDraftDataManager'
  import {
    getDeviceWritableMacroByteLength,
    getEditorRecordEncodedByteLength,
    MACRO_ENCODED_HEADER_BYTE_LENGTH,
  } from '../../utils/macroByteLength'
  import MacroTag from './MacroTag.vue'

  const { t } = useLocaleStore()

  const props = withDefaults(
    defineProps<{
      isRecording: boolean
      result?: MacroEditorAction[]
      maxMacroByteLength?: number
      enabledWheels?: WheelType[]
      coordinateMin?: number
      coordinateMax?: number
    }>(),
    {
      result: () => [],
      maxMacroByteLength: 512,
      enabledWheels: () => [],
      coordinateMin: -2048,
      coordinateMax: 2047,
    },
  )

  const emit = defineEmits<{
    (e: 'request-single-recording'): void
    (e: 'stop-recording'): void
    (e: 'dirty-change', dirty: boolean): void
    (e: 'state-change', state: { hasRecords: boolean; hasDraft: boolean }): void
  }>()

  const {
    workingData,
    displayData,
    generateRecordId,
    hasDraft,
    clearDraft,
    clearWorking,
    saveRecords: saveRecordsDM,
    resetOriginal,
    insertAt,
    updateAt,
    markAsDeleted,
    findIndexByRef,
  } = useDraftDataManager(props.result)

  const { registerCardRef, openDelayEditor, openCoordinateXEditor, findNextRecordId } =
    useCardRefs(displayData)

  const { singleCaptureActive, startSingleCapture, stopSingleCapture, tryConsumeCapture } =
    useSingleCapture(props.enabledWheels)

  const recordActions = useRecordActions({
    workingData,
    markAsDeleted,
    findIndexByRef,
  })

  const mutations = useRecordMutations({
    workingData,
    insertAt,
    updateAt,
    markAsDeleted,
    findIndexByRef,
  })

  const containerRef = ref<HTMLElement>()

  /**
   * 指派开关宏在极短延迟下会持续灌入按键。
   * 录制事件先写入 pending，再按动画帧一次性提交，避免每条记录都触发整表重绘。
   */
  const pendingRecords: DraftRecordItem[] = []
  let flushRafId = 0
  let recordingLimitReached = false

  const monitorState = computed(() => ({
    hasRecords: displayData.value.length > 0,
    hasDraft: hasDraft(),
  }))

  const emitStateChange = () => {
    emit('dirty-change', monitorState.value.hasDraft)
    emit('state-change', monitorState.value)
  }

  const getRecordByteLength = (record: MacroEditorAction) =>
    getEditorRecordEncodedByteLength(record)

  const getPendingByteLength = () =>
    pendingRecords.reduce((total, item) => total + getRecordByteLength(item), 0)

  const getDisplayDataByteLength = () =>
    displayData.value.reduce(
      (total, item) => total + getRecordByteLength(item),
      MACRO_ENCODED_HEADER_BYTE_LENGTH,
    ) + getPendingByteLength()

  const scrollMonitorToBottom = () => {
    containerRef.value?.scrollTo({
      top: containerRef.value.scrollHeight,
    })
  }

  const flushPendingRecords = () => {
    flushRafId = 0
    if (pendingRecords.length === 0) return

    workingData.value.push(...pendingRecords)
    pendingRecords.length = 0
    emitStateChange()
    scrollMonitorToBottom()
  }

  const cancelScheduledFlush = () => {
    if (!flushRafId) return
    cancelAnimationFrame(flushRafId)
    flushRafId = 0
  }

  const flushPendingRecordsNow = () => {
    cancelScheduledFlush()
    flushPendingRecords()
  }

  const discardPendingRecords = () => {
    cancelScheduledFlush()
    pendingRecords.length = 0
  }

  const scheduleFlush = () => {
    if (flushRafId) return
    flushRafId = requestAnimationFrame(flushPendingRecords)
  }

  const stopRecordingForLimit = () => {
    if (recordingLimitReached) return

    recordingLimitReached = true
    flushPendingRecordsNow()
    toast.error(t('toast.keys.macroRecordingLimit'))

    if (props.isRecording && !singleCaptureActive.value) {
      emit('stop-recording')
    }
  }

  const appendRecordToWorking = (
    record: MacroEditorAction,
    reservedRecords: MacroEditorAction[] = [],
  ) => {
    if (recordingLimitReached) return false

    const reservedByteLength = reservedRecords.reduce(
      (total, item) => total + getRecordByteLength(item),
      0,
    )
    const nextByteLength =
      getDisplayDataByteLength() + getRecordByteLength(record) + reservedByteLength
    // 协议上限里，写满槽位的短尾包设备不收。刚好不超过可写长度的动作保留。
    const writableByteLength = getDeviceWritableMacroByteLength(props.maxMacroByteLength)

    if (nextByteLength > writableByteLength) {
      stopRecordingForLimit()
      return false
    }

    const nextItem: DraftRecordItem = {
      ...record,
      __status: 'added',
      __id: generateRecordId(),
    }

    if (props.isRecording && !singleCaptureActive.value) {
      pendingRecords.push(nextItem)
      scheduleFlush()
      return true
    }

    workingData.value.push(nextItem)
    emitStateChange()
    return true
  }

  const handleCapturedForSingle = (payload: MacroEditorKeyAction | MacroEditorWheelAction) =>
    tryConsumeCapture(payload)

  const effectiveRecording = computed(() => props.isRecording || singleCaptureActive.value)
  const { attach, detach, resetRecorderState } = useMacroRecordingEvents({
    reference: containerRef,
    isRecording: effectiveRecording,
    onRecord: appendRecordToWorking,
    onCapturedForSingle: handleCapturedForSingle,
    enabledWheels: props.enabledWheels,
  })

  const beginSingleCapture = (
    mode: 'keyPair' | 'wheel' | 'action',
    callback: (payload: MacroEditorKeyAction | MacroEditorWheelAction) => void,
  ) => {
    resetRecorderState()
    containerRef.value?.focus()

    startSingleCapture(mode, callback, () => {
      emit('stop-recording')
    })

    emit('request-single-recording')

    nextTick(() => {
      containerRef.value?.focus()
    })
  }

  const beginSingleKeyEdit = (record: DraftRecordItem) => {
    // action 模式同时接受按键成对与滚轮，支持按键↔滚轮跨类型修改
    beginSingleCapture('action', (payload) => {
      if (record.type === 1) {
        if (payload.type === 1) {
          mutations.applyKeyEdit(record, payload.keystroke)
        } else if (payload.type === 3) {
          mutations.convertKeyToWheel(record, {
            vWheel: payload.vWheel,
            hWheel: payload.hWheel,
          })
        }
      }

      if (record.type === 3) {
        if (payload.type === 3) {
          mutations.updateWheel(record, {
            vWheel: payload.vWheel,
            hWheel: payload.hWheel,
          })
        } else if (payload.type === 1) {
          mutations.convertWheelToKey(record, payload.keystroke)
        }
      }

      emitStateChange()
    })
  }

  const beginSingleAddKey = (record: DraftRecordItem) => {
    const inserted = mutations.addKeyPlaceholderPair(record)
    if (!inserted) return
    emitStateChange()

    beginSingleCapture('keyPair', (payload) => {
      const keyPayload = payload as MacroEditorKeyAction
      if (keyPayload.type !== 1) return

      mutations.finalizeAddedKeyPair(record, keyPayload.keystroke)
      emitStateChange()
    })
  }

  const handleDelete = (record: DraftRecordItem) => {
    recordActions.deleteRecord(record)
    emitStateChange()
  }

  const handleEdit = async (record: DraftRecordItem, recordId?: string) => {
    if (record.type === 1 || record.type === 3) {
      beginSingleKeyEdit(record)
      return
    }

    if (record.type === 2) {
      await openCoordinateXEditor(recordId)
      return
    }

    await openDelayEditor(recordId)
  }

  const handleAddDelay = async (record: DraftRecordItem) => {
    const delayId = recordActions.addDelayAfterRecord(record, insertAt)
    emitStateChange()

    await openDelayEditor(delayId)
  }

  const handleAddCoordinate = async (record: DraftRecordItem, recordId?: string) => {
    const index = findIndexByRef(record)
    if (index < 0) return

    insertAt(index + 1, {
      type: 2,
      x_coordinate: 0,
      y_coordinate: 0,
    })
    emitStateChange()

    const nextId = findNextRecordId(recordId)
    await openCoordinateXEditor(nextId)
  }

  const { routeMainAction, routeSubAction } = useActionRouter({
    onDelete: (record) => handleDelete(record),
    onEdit: (record, id) => handleEdit(record, id),
    onAddKey: (record) => beginSingleAddKey(record),
    onAddDelay: (record) => handleAddDelay(record),
    onAddCoordinate: (record, id) => handleAddCoordinate(record, id),
  })

  const handleDelayUpdate = (record: DraftRecordItem, time: number) => {
    if (props.isRecording) return

    mutations.updateDelay(record, time)
    emitStateChange()
  }

  const handleCoordinateUpdate = (
    record: DraftRecordItem,
    payload: { x_coordinate?: number; y_coordinate?: number },
  ) => {
    if (props.isRecording) return

    mutations.updateCoordinate(record, payload)
    emitStateChange()
  }

  const clearAllRecords = () => {
    discardPendingRecords()
    clearWorking()
    emitStateChange()
  }

  const clearUnsavedRecords = () => {
    discardPendingRecords()
    clearDraft()
    emitStateChange()
  }

  const saveRecords = () => {
    flushPendingRecordsNow()
    const result = saveRecordsDM()
    emitStateChange()
    return result
  }

  const hasDraftRecords = () => {
    return hasDraft()
  }

  watch(
    () => props.result,
    (value) => {
      discardPendingRecords()
      resetOriginal(value)
      emitStateChange()
    },
    { deep: true, immediate: true },
  )

  watch(
    () => props.isRecording,
    (recording) => {
      if (recording) {
        recordingLimitReached = false
        resetRecorderState()
        nextTick(() => {
          containerRef.value?.focus()
        })
        return
      }

      flushPendingRecordsNow()
      resetRecorderState()
      stopSingleCapture()
    },
    { immediate: true },
  )

  onMounted(() => {
    attach()
  })

  onUnmounted(() => {
    discardPendingRecords()
    detach()
  })

  const applyDelayOverride = (time: number) => {
    flushPendingRecordsNow()
    workingData.value.forEach((item, index) => {
      if (item.type === 4 && item.__status !== 'deleted' && item.time !== time) {
        updateAt(index, { time })
      }
    })
    emitStateChange()
  }

  defineExpose({
    clearAllRecords,
    clearUnsavedRecords,
    saveRecords,
    hasDraftRecords,
    applyDelayOverride,
    containerRef,
  })
</script>

<style scoped></style>
